package com.evoapps.evoshift.viewmodels

import com.evoapps.evoshift.domain.util.AppClock
import com.evoapps.evoshift.domain.usecase.user.PersonNames
import com.evoapps.evoshift.domain.usecase.user.ResolvePersonNamesUseCase
import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.evoapps.evoshift.domain.model.BreakRules
import com.evoapps.evoshift.domain.model.ClockReviewAction
import com.evoapps.evoshift.domain.model.ClockReviewSettings
import com.evoapps.evoshift.domain.model.RosterIds
import com.evoapps.evoshift.domain.repository.ClockPhotoRepository
import com.evoapps.evoshift.domain.repository.ShiftRepository
import com.evoapps.evoshift.domain.repository.UserRepository
import com.evoapps.evoshift.domain.repository.WorkplaceRepository
import com.evoapps.evoshift.domain.usecase.auth.GetCurrentUserUseCase
import com.evoapps.evoshift.domain.usecase.billing.ObservePlanActiveUseCase
import com.evoapps.evoshift.domain.usecase.shift.ClockReviewItem
import com.evoapps.evoshift.domain.usecase.shift.ReviewClockUseCase
import com.evoapps.evoshift.domain.usecase.shift.clockReviewItems
import com.evoapps.evoshift.domain.usecase.timeZoneOrNull
import com.evoapps.evoshift.session.WorkplaceSessionManager
import com.evoapps.evoshift.ui.util.UiText
import com.evoapps.evoshift.ui.util.toUiText
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.clock_review_done_adjusted
import evoshift.composeapp.generated.resources.clock_review_done_approved
import evoshift.composeapp.generated.resources.clock_review_done_confirmed
import evoshift.composeapp.generated.resources.clock_review_done_edited
import evoshift.composeapp.generated.resources.clock_review_done_rejected
import evoshift.composeapp.generated.resources.clock_review_edit_invalid
import evoshift.composeapp.generated.resources.error_no_pay_rates
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.flatMapLatest
import kotlinx.coroutines.flow.flowOf
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlinx.datetime.LocalTime
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import kotlin.time.Instant

/** The "Edit times" dialog: what is typed so far ("HH:mm"; the clock-out empty while working). */
data class ClockEditDraft(val item: ClockReviewItem, val start: String, val end: String)

/** The photo dialog of an item: loading, the JPEG, or none saved. */
data class ClockPhotoView(val shiftId: String, val loading: Boolean = true, val jpeg: ByteArray? = null, val missing: Boolean = false) {
    override fun equals(other: Any?): Boolean =
        other is ClockPhotoView && other.shiftId == shiftId && other.loading == loading && other.missing == missing &&
            (other.jpeg?.contentEquals(jpeg) ?: (jpeg == null))

    override fun hashCode(): Int = shiftId.hashCode() * 31 + loading.hashCode() + (jpeg?.contentHashCode() ?: 0)
}

data class ClockReviewUiState(
    val isLoading: Boolean = true,
    /** Whether the reader sees the list at all (managers: TODAY_AT_WORK). */
    val visible: Boolean = false,
    /** Whether they may decide (CLOCK_REVIEW_DECIDING: a manager while the plan runs). */
    val canDecide: Boolean = false,
    val items: List<ClockReviewItem> = emptyList(),
    /** An item being decided (its buttons wait). */
    val busyShiftId: String? = null,
    val editing: ClockEditDraft? = null,
    val photo: ClockPhotoView? = null,
    val message: UiText? = null,
    val error: UiText? = null
)

/**
 * SH-134: the managers' "Do sprawdzenia" list: flagged clock times and unplanned clock-ins of one
 * day at one workplace, with the decisions (approve as is, adjust to the plan, edit the times,
 * confirm or reject an unplanned clock-in). Shown in Today at work ([showToday]: the current
 * workplace, today in its time zone) and in the daily records ([showDay]). A decision leaves the
 * list at once and comes back if the server refuses it.
 */
@OptIn(ExperimentalCoroutinesApi::class)
class ClockReviewViewModel(
    private val getCurrentUserUseCase: GetCurrentUserUseCase,
    private val sessionManager: WorkplaceSessionManager,
    private val shiftRepository: ShiftRepository,
    private val workplaceRepository: WorkplaceRepository,
    private val userRepository: UserRepository,
    private val reviewClock: ReviewClockUseCase,
    private val photos: ClockPhotoRepository,
    private val plans: ObservePlanActiveUseCase? = null,
    private val now: () -> Instant = { AppClock.now() }
) : ViewModel() {

    private val _uiState = MutableStateFlow(ClockReviewUiState())
    val uiState: StateFlow<ClockReviewUiState> = _uiState.asStateFlow()

    /** Which day of which workplace; null workplace: the current one, today. */
    private data class Target(val workplaceId: String?, val rosterId: String?, val date: String?)

    private val target = MutableStateFlow<Target?>(null)
    private var loading: Job? = null
    /** Items decided but not yet confirmed by the next snapshot (kept hidden meanwhile). */
    private val decided = mutableSetOf<String>()

    /** Today at work: the current workplace's day. */
    fun showToday() {
        target.value = Target(null, null, null)
        start()
    }

    /** The daily records' day. */
    fun showDay(workplaceId: String, rosterId: String, date: String) {
        target.value = Target(workplaceId, rosterId, date)
        start()
    }

    private fun start() {
        if (loading != null) return
        loading = viewModelScope.launch {
            target.flatMapLatest { t ->
                if (t == null) flowOf(null)
                else if (t.workplaceId != null) flowOf(t)
                else sessionManager.currentWorkplaceId.map { wp -> wp?.let { Target(it, null, null) } }
            }.distinctUntilChanged().flatMapLatest { t ->
                if (t?.workplaceId == null) return@flatMapLatest flowOf(ClockReviewUiState(isLoading = false))
                val wp = t.workplaceId
                val workplace = workplaceRepository.getWorkplace(wp).getOrNull()
                val settings = workplace?.settings?.clockReview ?: ClockReviewSettings()
                val date = t.date ?: now().toLocalDateTime(timeZoneOrNull(workplace?.settings?.timezone) ?: TimeZone.currentSystemDefault()).date.toString()
                val rosterId = t.rosterId ?: RosterIds.of(wp, date.take(4).toInt(), date.substring(5, 7).toInt())
                // SH-235: the shared resolver (the team, then the profile of anyone else on the day).
                val resolveNames = ResolvePersonNamesUseCase(userRepository)
                val names = userRepository.getAssignableEmployees(wp).getOrNull().orEmpty()
                    .associateTo(HashMap()) { it.uid to it.displayName }
                val asked = HashSet<String>()
                capabilitiesAt(getCurrentUserUseCase(), plans, wp).flatMapLatest { caps ->
                    if (!caps.shows(Gate.TODAY_AT_WORK)) flowOf(ClockReviewUiState(isLoading = false))
                    else shiftRepository.observeShiftsForRoster(rosterId).map { shifts ->
                        val day = shifts.filter { it.date == date }
                        val unknown = resolveNames.peopleOn(day).filter { it !in names && asked.add(it) }
                        if (unknown.isNotEmpty()) names += resolveNames(wp, unknown, team = emptyList())
                        val items = clockReviewItems(day, settings) { names[it] ?: PersonNames.UNNAMED }
                        decided.retainAll(items.map { it.shiftId }.toSet())
                        ClockReviewUiState(
                            isLoading = false,
                            visible = true,
                            canDecide = caps.shows(Gate.CLOCK_REVIEW_DECIDING),
                            items = items.filter { it.shiftId !in decided }
                        )
                    }
                }
            }.catch { e -> _uiState.update { it.copy(isLoading = false, error = e.toUiText()) } } // SH-188
                .collect { loaded ->
                _uiState.update { state ->
                    loaded.copy(
                        busyShiftId = state.busyShiftId,
                        editing = state.editing,
                        photo = state.photo,
                        message = state.message,
                        error = state.error
                    )
                }
            }
        }
    }

    fun approve(item: ClockReviewItem) = decide(item, ClockReviewAction.APPROVE, Res.string.clock_review_done_approved)

    fun adjustToPlan(item: ClockReviewItem) = decide(item, ClockReviewAction.ADJUST_TO_PLAN, Res.string.clock_review_done_adjusted)

    fun confirmUnplanned(item: ClockReviewItem) = decide(item, ClockReviewAction.CONFIRM_UNPLANNED, Res.string.clock_review_done_confirmed)

    fun rejectUnplanned(item: ClockReviewItem) = decide(item, ClockReviewAction.REJECT_UNPLANNED, Res.string.clock_review_done_rejected)

    fun startEdit(item: ClockReviewItem) {
        if (!_uiState.value.canDecide) return
        _uiState.update {
            it.copy(editing = ClockEditDraft(item, (item.actualStart ?: item.plannedStart).hhmm(), item.actualEnd?.hhmm().orEmpty()))
        }
    }

    fun updateEditStart(text: String) = _uiState.update { s -> s.copy(editing = s.editing?.copy(start = text.take(5))) }

    fun updateEditEnd(text: String) = _uiState.update { s -> s.copy(editing = s.editing?.copy(end = text.take(5))) }

    fun dismissEdit() = _uiState.update { it.copy(editing = null) }

    /** Saves the typed times: the clock-in on the shift's clock-in day, the clock-out after it (next day if earlier). */
    fun saveEdit() {
        val draft = _uiState.value.editing ?: return
        val day = (draft.item.actualStart ?: draft.item.plannedStart).take(10)
        val start = parseClock(draft.start)?.let { "${day}T$it:00" }
        val end = if (draft.end.isBlank()) "" else parseClock(draft.end)?.let { BreakRules.atClock(start ?: return@let null, it) }
        if (start == null || end == null || (end.isNotEmpty() && end <= start)) {
            _uiState.update { it.copy(error = UiText.Resource(Res.string.clock_review_edit_invalid)) }
            return
        }
        _uiState.update { it.copy(editing = null) }
        decide(draft.item, ClockReviewAction.EDIT_TIMES, Res.string.clock_review_done_edited, start, end)
    }

    fun openPhoto(item: ClockReviewItem) {
        val path = item.photoPath
        _uiState.update { it.copy(photo = ClockPhotoView(item.shiftId, loading = path != null, missing = path == null)) }
        if (path == null) return
        viewModelScope.launch {
            val jpeg = photos.download(path).getOrNull()
            _uiState.update { s ->
                if (s.photo?.shiftId != item.shiftId) s
                else s.copy(photo = ClockPhotoView(item.shiftId, loading = false, jpeg = jpeg, missing = jpeg == null))
            }
        }
    }

    fun closePhoto() = _uiState.update { it.copy(photo = null) }

    fun messageShown() = _uiState.update { it.copy(message = null, error = null) }

    private fun decide(
        item: ClockReviewItem,
        action: ClockReviewAction,
        done: org.jetbrains.compose.resources.StringResource,
        start: String? = null,
        end: String? = null
    ) {
        val state = _uiState.value
        if (!state.canDecide || state.busyShiftId != null) return
        val workplaceId = RosterIds.workplaceIdOf(item.rosterId)
        // Optimistic: off the list at once; back if the server refuses.
        decided += item.shiftId
        _uiState.update { s -> s.copy(items = s.items.filter { it.shiftId != item.shiftId }, busyShiftId = item.shiftId, message = null, error = null) }
        viewModelScope.launch {
            reviewClock(workplaceId, item.rosterId, item.shiftId, item.date, action, start, end).fold(
                onSuccess = { outcome ->
                    _uiState.update {
                        it.copy(
                            busyShiftId = null,
                            message = if (outcome.missingPayRates) UiText.Resource(Res.string.error_no_pay_rates) else UiText.Resource(done)
                        )
                    }
                },
                onFailure = { e ->
                    decided -= item.shiftId
                    _uiState.update { s ->
                        s.copy(
                            busyShiftId = null,
                            items = (s.items + item).distinctBy { it.shiftId }
                                .sortedWith(compareBy({ it.date }, { it.actualStart ?: it.plannedStart }, { it.workerName })),
                            error = e.toUiText()
                        )
                    }
                }
            )
        }
    }

    private fun String.hhmm(): String = substringAfter('T').take(5)

    private fun parseClock(text: String): String? {
        val t = text.trim().replace('.', ':')
        val normal = if (t.length == 4 && t[1] == ':') "0$t" else t
        return runCatching { LocalTime.parse(normal) }.getOrNull()?.let { normal.take(5) }
    }
}
